import { __assign, __extends, __read, __spreadArray } from "tslib";
import { isEqual, isNil, isObject } from '@antv/util';
import { mat4, quat, vec3 } from 'gl-matrix';
import { DisplayObjectPool } from '../DisplayObjectPool';
import { KeyframeEffect } from '../dom/KeyframeEffect';
import { Element } from '../dom/Element';
import { ElementEvent } from '../dom/interfaces';
import { Shape } from '../types';
import { createVec3, formatAttribute, fromRotationTranslationScale, getEuler, rad2deg } from '../utils';
import { globalContainer, stylePropertyParserFactory, stylePropertyUpdaterFactory } from '../global-module';
import { dirtifyToRoot } from '../services';
import { MutationEvent } from '../dom/MutationEvent';
var DEFAULT_STYLE_PROPS = {
  anchor: [0, 0, 0],
  origin: [0, 0, 0],
  opacity: 1,
  fillOpacity: 1,
  strokeOpacity: 1,
  fill: 'transparent',
  stroke: 'transparent'
};
/**
 * prototype chains: DisplayObject -> Element -> Node -> EventTarget
 *
 * mixins: Animatable, Transformable, Visible
 * @see https://github.com/tannerntannern/ts-mixer/blob/master/README.md#mixing-generic-classes
 *
 * Provide abilities in scene graph, such as:
 * * transform `translate/rotate/scale`
 * * add/remove child
 * * visibility and z-index
 *
 * Those abilities are implemented with those components: `Transform/Sortable/Visible`.
 *
 * Emit following events:
 * * init
 * * destroy
 * * attributeChanged
 */

var DisplayObject =
/** @class */
function (_super) {
  __extends(DisplayObject, _super);

  function DisplayObject(config) {
    var _this = this;

    var _a, _b, _c;

    _this = _super.call(this) || this;
    /**
     * push to active animations after calling `animate()`
     */

    _this.activeAnimations = []; // assign name, id to config
    // eg. group.get('name')

    _this.config = config; // compatible with G 3.0

    _this.config.interactive = (_a = _this.config.capture) !== null && _a !== void 0 ? _a : _this.config.interactive; // init scene graph node

    _this.id = _this.config.id || '';
    _this.name = _this.config.name || '';

    if (_this.config.className || _this.config.class) {
      _this.className = _this.config.className || _this.config.class;
    }

    _this.interactive = (_b = _this.config.interactive) !== null && _b !== void 0 ? _b : true;
    _this.nodeName = _this.config.type || Shape.GROUP; // compatible with G 3.0

    _this.config.style = __assign(__assign(__assign(__assign({}, DEFAULT_STYLE_PROPS), {
      zIndex: (_c = _this.config.zIndex) !== null && _c !== void 0 ? _c : 0,
      visibility: _this.config.visible === false ? 'hidden' : 'visible'
    }), _this.config.style), _this.config.attrs);
    _this.style = new Proxy(__assign(__assign({}, _this.attributes), {
      setProperty: function setProperty(propertyName, value) {
        _this.setAttribute(propertyName, value);
      },
      getPropertyValue: function getPropertyValue(propertyName) {
        return _this.getAttribute(propertyName);
      },
      removeProperty: function removeProperty(propertyName) {
        _this.removeAttribute(propertyName);
      },
      item: function item() {
        return '';
      }
    }), {
      get: function get(target, name) {
        if (name in target) {
          return target[name];
        }

        return _this.getAttribute(name);
      },
      set: function set(_, prop, value) {
        _this.setAttribute(prop, value);

        return true;
      }
    });

    _this.initAttributes(_this.config.style); // insert this group into pool


    globalContainer.get(DisplayObjectPool).add(_this.entity, _this);
    return _this;
  }

  DisplayObject.prototype.destroy = function () {
    _super.prototype.destroy.call(this); // remove from into pool


    globalContainer.get(DisplayObjectPool).remove(this.entity); // stop all active animations

    this.getAnimations().forEach(function (animation) {
      animation.cancel();
    });
  };

  DisplayObject.prototype.cloneNode = function (deep) {
    var cloned = new this.constructor({
      // copy id & name
      // @see https://developer.mozilla.org/en-US/docs/Web/API/Node/cloneNode#notes
      id: this.id,
      name: this.name,
      className: this.name,
      interactive: this.interactive,
      style: __assign({}, this.attributes)
    });

    if (deep) {
      this.children.forEach(function (child) {
        var clonedChild = child.cloneNode(deep);
        cloned.appendChild(clonedChild);
      });
    }

    return cloned;
  };

  DisplayObject.prototype.initAttributes = function (attributes) {
    var _this = this;

    if (attributes === void 0) {
      attributes = {};
    }

    var renderable = this.renderable; // parse attributes first

    for (var attributeName in attributes) {
      var _a = __read(formatAttribute(attributeName, attributes[attributeName]), 2),
          name_1 = _a[0],
          value = _a[1];

      this.attributes[name_1] = value; // @ts-ignore

      this.parseStyleProperty(name_1, value);
    }

    var priorities = {
      x: Infinity,
      y: Infinity
    }; // update x, y at last

    var sortedNames = Object.keys(attributes).sort(function (a, b) {
      return (priorities[a] || 0) - (priorities[b] || 0);
    });
    sortedNames.forEach(function (attributeName) {
      var _a = __read(formatAttribute(attributeName, ''), 1),
          name = _a[0]; // @ts-ignore


      _this.updateStyleProperty(name, undefined, _this.parsedStyle[name]);
    }); // redraw at next frame

    renderable.dirty = true;
  };

  DisplayObject.prototype.setAttribute = function (name, value, force) {
    if (force === void 0) {
      force = false;
    }

    var _a = __read(formatAttribute(name, value), 2),
        attributeName = _a[0],
        attributeValue = _a[1];

    if (force || !isEqual(attributeValue, this.attributes[attributeName]) || attributeName === 'transformOrigin' || attributeName === 'visibility' // will affect children
    ) {
      if (attributeName === 'visibility') {
        // set value cascade
        this.forEach(function (object) {
          object.changeAttribute(attributeName, attributeValue);
        });
      } else {
        this.changeAttribute(attributeName, attributeValue);
      }

      _super.prototype.setAttribute.call(this, attributeName, attributeValue);
    }
  };
  /**
   * parse property, eg.
   * * fill: 'red' => [1, 0, 0, 1]
   * * translateX: '10px' => { unit: 'px', value: 10 }
   * * fontSize: '2em' => { unit: 'px', value: 32 }
   */


  DisplayObject.prototype.parseStyleProperty = function (name, value) {
    // const stylePropertyParser = this.stylePropertyParserFactory(name);
    var stylePropertyParser = stylePropertyParserFactory[name];

    if (stylePropertyParser) {
      // @ts-ignore
      this.parsedStyle[name] = stylePropertyParser(value, this, name);
    } else {
      this.parsedStyle[name] = value;
    }
  };

  DisplayObject.prototype.updateStyleProperty = function (name, oldParsedValue, newParsedValue) {
    var _this = this; // @ts-ignore


    var stylePropertyUpdaters = stylePropertyUpdaterFactory[name];

    if (stylePropertyUpdaters) {
      stylePropertyUpdaters.forEach(function (updater) {
        // @ts-ignore
        updater(oldParsedValue, newParsedValue, _this, _this.sceneGraphService, name);
      });
    }
  };
  /**
   * called when attributes get changed or initialized
   */


  DisplayObject.prototype.changeAttribute = function (name, value) {
    var _this = this;

    var renderable = this.renderable;
    var oldValue = this.attributes[name]; // @ts-ignore

    var oldParsedValue = this.parsedStyle[name]; // update value

    this.attributes[name] = value; // @ts-ignore

    this.parseStyleProperty(name, value); // @ts-ignore

    var newParsedValue = this.parsedStyle[name]; // @ts-ignore

    this.updateStyleProperty(name, oldParsedValue, newParsedValue); // inform clip path targets

    if (this.attributes.clipPathTargets && this.attributes.clipPathTargets.length) {
      this.attributes.clipPathTargets.forEach(function (target) {
        dirtifyToRoot(target); // target.emit(ElementEvent.ATTR_MODIFIED, {
        //   attributeName: 'clipPath',
        //   oldValue: this,
        //   newValue: this,
        // });

        target.dispatchEvent(new MutationEvent(ElementEvent.ATTR_MODIFIED, target, _this, _this, 'clipPath', MutationEvent.MODIFICATION, _this, _this));
      });
    } // redraw at next frame


    renderable.dirty = true;
    this.dispatchEvent(new MutationEvent(ElementEvent.ATTR_MODIFIED, this, oldValue, value, name, MutationEvent.MODIFICATION, oldParsedValue, newParsedValue)); // this.emit(ElementEvent.ATTR_MODIFIED, {
    //   attrName: name,
    //   attributeName: name,
    //   prevValue: oldValue,
    //   oldValue,
    //   newValue: value,
    //   oldParsedValue,
    //   newParsedValue,
    // });
  }; // #region transformable


  DisplayObject.prototype.setOrigin = function (position, y, z) {
    if (y === void 0) {
      y = 0;
    }

    if (z === void 0) {
      z = 0;
    }

    this.sceneGraphService.setOrigin(this, createVec3(position, y, z));
    this.attributes.origin = this.getOrigin();
    return this;
  };

  DisplayObject.prototype.getOrigin = function () {
    return this.sceneGraphService.getOrigin(this);
  };
  /**
   * set position in world space
   */


  DisplayObject.prototype.setPosition = function (position, y, z) {
    if (y === void 0) {
      y = 0;
    }

    if (z === void 0) {
      z = 0;
    }

    this.sceneGraphService.setPosition(this, createVec3(position, y, z));
    this.syncLocalPosition();
    return this;
  };
  /**
   * set position in local space
   */


  DisplayObject.prototype.setLocalPosition = function (position, y, z) {
    if (y === void 0) {
      y = 0;
    }

    if (z === void 0) {
      z = 0;
    }

    this.sceneGraphService.setLocalPosition(this, createVec3(position, y, z));
    this.syncLocalPosition();
    return this;
  };
  /**
   * translate in world space
   */


  DisplayObject.prototype.translate = function (position, y, z) {
    if (y === void 0) {
      y = 0;
    }

    if (z === void 0) {
      z = 0;
    }

    this.sceneGraphService.translate(this, createVec3(position, y, z));
    this.syncLocalPosition();
    return this;
  };
  /**
   * translate in local space
   */


  DisplayObject.prototype.translateLocal = function (position, y, z) {
    if (y === void 0) {
      y = 0;
    }

    if (z === void 0) {
      z = 0;
    }

    this.sceneGraphService.translateLocal(this, createVec3(position, y, z));
    this.syncLocalPosition();
    return this;
  };

  DisplayObject.prototype.getPosition = function () {
    return this.sceneGraphService.getPosition(this);
  };

  DisplayObject.prototype.getLocalPosition = function () {
    return this.sceneGraphService.getLocalPosition(this);
  };
  /**
   * compatible with G 3.0
   *
   * scaling in local space
   * scale(10) = scale(10, 10, 10)
   *
   * we can't set scale in world space
   */


  DisplayObject.prototype.scale = function (scaling, y, z) {
    return this.scaleLocal(scaling, y, z);
  };

  DisplayObject.prototype.scaleLocal = function (scaling, y, z) {
    if (typeof scaling === 'number') {
      y = y || scaling;
      z = z || scaling;
      scaling = createVec3(scaling, y, z);
    }

    this.sceneGraphService.scaleLocal(this, scaling);
    return this;
  };
  /**
   * set scaling in local space
   */


  DisplayObject.prototype.setLocalScale = function (scaling, y, z) {
    if (typeof scaling === 'number') {
      y = y || scaling;
      z = z || scaling;
      scaling = createVec3(scaling, y, z);
    }

    this.sceneGraphService.setLocalScale(this, scaling);
    return this;
  };
  /**
   * get scaling in local space
   */


  DisplayObject.prototype.getLocalScale = function () {
    return this.sceneGraphService.getLocalScale(this);
  };
  /**
   * get scaling in world space
   */


  DisplayObject.prototype.getScale = function () {
    return this.sceneGraphService.getScale(this);
  };
  /**
   * only return degrees of Z axis in world space
   */


  DisplayObject.prototype.getEulerAngles = function () {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    var _a = __read(getEuler(vec3.create(), this.sceneGraphService.getWorldTransform(this)), 3),
        ex = _a[0],
        ey = _a[1],
        ez = _a[2];

    return rad2deg(ez);
  };
  /**
   * only return degrees of Z axis in local space
   */


  DisplayObject.prototype.getLocalEulerAngles = function () {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    var _a = __read(getEuler(vec3.create(), this.sceneGraphService.getLocalRotation(this)), 3),
        ex = _a[0],
        ey = _a[1],
        ez = _a[2];

    return rad2deg(ez);
  };
  /**
   * set euler angles(degrees) in world space
   */


  DisplayObject.prototype.setEulerAngles = function (z) {
    this.sceneGraphService.setEulerAngles(this, 0, 0, z);
    return this;
  };
  /**
   * set euler angles(degrees) in local space
   */


  DisplayObject.prototype.setLocalEulerAngles = function (z) {
    this.sceneGraphService.setLocalEulerAngles(this, 0, 0, z);
    return this;
  };

  DisplayObject.prototype.rotateLocal = function (x, y, z) {
    if (isNil(y) && isNil(z)) {
      this.sceneGraphService.rotateLocal(this, 0, 0, x);
    } else {
      this.sceneGraphService.rotateLocal(this, x, y, z);
    }

    return this;
  };

  DisplayObject.prototype.rotate = function (x, y, z) {
    if (isNil(y) && isNil(z)) {
      this.sceneGraphService.rotate(this, 0, 0, x);
    } else {
      this.sceneGraphService.rotate(this, x, y, z);
    }

    return this;
  };

  DisplayObject.prototype.setRotation = function (rotation, y, z, w) {
    this.sceneGraphService.setRotation(this, rotation, y, z, w);
    return this;
  };

  DisplayObject.prototype.setLocalRotation = function (rotation, y, z, w) {
    this.sceneGraphService.setLocalRotation(this, rotation, y, z, w);
    return this;
  };

  DisplayObject.prototype.getRotation = function () {
    return this.sceneGraphService.getRotation(this);
  };

  DisplayObject.prototype.getLocalRotation = function () {
    return this.sceneGraphService.getLocalRotation(this);
  };

  DisplayObject.prototype.getLocalTransform = function () {
    return this.sceneGraphService.getLocalTransform(this);
  };

  DisplayObject.prototype.getWorldTransform = function () {
    return this.sceneGraphService.getWorldTransform(this);
  };

  DisplayObject.prototype.resetLocalTransform = function () {
    this.sceneGraphService.resetLocalTransform(this);
  };
  /**
   * sync style.x/y when local position changed
   *
   * Mixins may not declare private/protected properties
   * however, you can use ES2020 private fields
   */


  DisplayObject.prototype.syncLocalPosition = function () {
    var localPosition = this.getLocalPosition();
    this.attributes.x = localPosition[0];
    this.attributes.y = localPosition[1];
  }; // #endregion transformable
  // #region animatable

  /**
   * returns an array of all Animation objects affecting this element
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Element/getAnimations
   */


  DisplayObject.prototype.getAnimations = function () {
    return this.activeAnimations;
  };
  /**
   * create an animation with WAAPI
   * @see https://developer.mozilla.org/zh-CN/docs/Web/API/Element/animate
   */


  DisplayObject.prototype.animate = function (keyframes, options) {
    var _a, _b;

    var timeline = (_a = this.ownerDocument) === null || _a === void 0 ? void 0 : _a.timeline; // accounte for clip path, use target's timeline

    if (this.attributes.clipPathTargets && this.attributes.clipPathTargets.length) {
      var target = this.attributes.clipPathTargets[0];
      timeline = (_b = target.ownerDocument) === null || _b === void 0 ? void 0 : _b.timeline;
    } // clear old parsed transform


    this.parsedStyle.transform = undefined;

    if (timeline) {
      return timeline.play(new KeyframeEffect(this, keyframes, options));
    }

    return null;
  }; // #endregion animatable
  // #region visible

  /**
   * show group, which will also change visibility of its children in sceneGraphNode
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/visibility
   */


  DisplayObject.prototype.show = function () {
    this.style.visibility = 'visible';
  };
  /**
   * hide group, which will also change visibility of its children in sceneGraphNode
   */


  DisplayObject.prototype.hide = function () {
    this.style.visibility = 'hidden';
  };

  DisplayObject.prototype.isVisible = function () {
    var cullable = this.cullable;
    return this.getAttribute('visibility') === 'visible' && (!cullable || cullable && !cullable.isCulled());
  };
  /**
   * bring to front in current group
   */


  DisplayObject.prototype.toFront = function () {
    if (this.parentNode) {
      this.style.zIndex = Math.max.apply(Math, __spreadArray([], __read(this.parentNode.children.map(function (child) {
        return Number(child.style.zIndex);
      })), false)) + 1;
    }

    return this;
  };
  /**
   * send to back in current group
   */


  DisplayObject.prototype.toBack = function () {
    if (this.parentNode) {
      this.style.zIndex = Math.min.apply(Math, __spreadArray([], __read(this.parentNode.children.map(function (child) {
        return Number(child.style.zIndex);
      })), false)) - 1;
    }

    return this;
  }; // #endregion visible
  // #region deprecated

  /**
   * @deprecated
   * compatible with G 3.0
   * @deprecated
   */


  DisplayObject.prototype.getCount = function () {
    return this.childElementCount;
  };
  /**
   * @deprecated
   * compatible with G 3.0
   * @deprecated
   */


  DisplayObject.prototype.getParent = function () {
    return this.parentElement;
  };
  /**
   * @deprecated
   * compatible with G 3.0
   * @deprecated
   */


  DisplayObject.prototype.getChildren = function () {
    return this.children;
  };
  /**
   * @deprecated
   * compatible with G 3.0
   * @deprecated
   */


  DisplayObject.prototype.getFirst = function () {
    return this.firstElementChild;
  };
  /**
   * @deprecated
   * compatible with G 3.0
   * @deprecated
   * get last child group/shape
   */


  DisplayObject.prototype.getLast = function () {
    return this.lastElementChild;
  };
  /**
   * @deprecated
   * compatible with G 3.0
   * @deprecated
   * get child group/shape by index
   */


  DisplayObject.prototype.getChildByIndex = function (index) {
    return this.children[index] || null;
  };
  /**
   * compatible with G 3.0
   * @deprecated
   */


  DisplayObject.prototype.add = function (child, index) {
    return this.appendChild(child, index);
  };
  /**
   * @alias style.clipPath
   * @deprecated
   */


  DisplayObject.prototype.setClip = function (clipPath) {
    this.style.clipPath = clipPath;
  };
  /**
   * @alias style.clipPath
   * @deprecated
   */


  DisplayObject.prototype.getClip = function () {
    return this.style.clipPath || null;
  };
  /**
   * compatible with G 3.0
   * @deprecated
   */


  DisplayObject.prototype.set = function (name, value) {
    this.config[name] = value;
  };
  /**
   * compatible with G 3.0
   * @deprecated
   */


  DisplayObject.prototype.get = function (name) {
    return this.config[name];
  };
  /**
   * compatible with G 3.0
   * @alias object.config
   * @deprecated
   */


  DisplayObject.prototype.getConfig = function () {
    return this.config;
  };

  DisplayObject.prototype.attr = function () {
    var _this = this;

    var args = [];

    for (var _i = 0; _i < arguments.length; _i++) {
      args[_i] = arguments[_i];
    }

    var _a = __read(args, 2),
        name = _a[0],
        value = _a[1];

    if (!name) {
      return this.attributes;
    }

    if (isObject(name)) {
      Object.keys(name).forEach(function (key) {
        _this.setAttribute(key, name[key]);
      });
      return this;
    }

    if (args.length === 2) {
      this.setAttribute(name, value);
      return this;
    }

    return this.attributes[name];
  };
  /**
   * @alias setPosition
   * @deprecated
   */


  DisplayObject.prototype.moveTo = function (position, y, z) {
    if (y === void 0) {
      y = 0;
    }

    if (z === void 0) {
      z = 0;
    }

    this.setPosition(position, y, z);
    return this;
  };
  /**
   * @alias setPosition
   * @deprecated
   */


  DisplayObject.prototype.move = function (position, y, z) {
    if (y === void 0) {
      y = 0;
    }

    if (z === void 0) {
      z = 0;
    }

    this.setPosition(position, y, z);
    return this;
  };
  /**
   * @alias style.zIndex
   * @deprecated
   */


  DisplayObject.prototype.setZIndex = function (zIndex) {
    this.style.zIndex = zIndex;
    return this;
  };
  /**
   * return 3x3 matrix in world space
   * @deprecated
   */


  DisplayObject.prototype.getMatrix = function () {
    var transform = this.getWorldTransform();

    var _a = __read(mat4.getTranslation(vec3.create(), transform), 2),
        tx = _a[0],
        ty = _a[1];

    var _b = __read(mat4.getScaling(vec3.create(), transform), 2),
        sx = _b[0],
        sy = _b[1];

    var rotation = mat4.getRotation(quat.create(), transform); // eslint-disable-next-line @typescript-eslint/no-unused-vars

    var _c = __read(getEuler(vec3.create(), rotation), 3),
        eux = _c[0],
        euy = _c[1],
        euz = _c[2]; // gimbal lock at 90 degrees


    return fromRotationTranslationScale(eux || euz, tx, ty, sx, sy);
  };
  /**
   * set 3x3 matrix in world space
   * @deprecated
   */


  DisplayObject.prototype.setMatrix = function (mat) {
    var row0x = mat[0];
    var row0y = mat[3];
    var row1x = mat[1];
    var row1y = mat[4]; // decompose 3x3 matrix
    // @see https://www.w3.org/TR/css-transforms-1/#decomposing-a-2d-matrix

    var scalingX = Math.sqrt(row0x * row0x + row0y * row0y);
    var scalingY = Math.sqrt(row1x * row1x + row1y * row1y); // If determinant is negative, one axis was flipped.

    var determinant = row0x * row1y - row0y * row1x;

    if (determinant < 0) {
      // Flip axis with minimum unit vector dot product.
      if (row0x < row1y) {
        scalingX = -scalingX;
      } else {
        scalingY = -scalingY;
      }
    } // Renormalize matrix to remove scale.


    if (scalingX) {
      row0x *= 1 / scalingX;
      row0y *= 1 / scalingX;
    }

    if (scalingY) {
      row1x *= 1 / scalingY;
      row1y *= 1 / scalingY;
    } // Compute rotation and renormalize matrix.


    var angle = Math.atan2(row0y, row0x);
    this.setEulerAngles(angle).setPosition(mat[6], mat[7]).setLocalScale(scalingX, scalingY);
  };

  return DisplayObject;
}(Element);

export { DisplayObject };